import { ReactNode } from 'react';
import { PiggyBank, TrendingUp, TrendingDown, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import type { SavingsRate } from '../../types';

interface SavingsRateCardProps {
  savingsRate: SavingsRate;
  formatCurrency: (amount: number) => string;
  carouselElement?: ReactNode;
}

const statusIcons = {
  excellent: CheckCircle,
  good: TrendingUp,
  average: TrendingUp,
  low: AlertTriangle,
  negative: TrendingDown,
};

const statusColors = {
  excellent: { color: 'text-green-600', bgColor: 'bg-green-100' },
  good: { color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  average: { color: 'text-amber-600', bgColor: 'bg-amber-100' },
  low: { color: 'text-orange-600', bgColor: 'bg-orange-100' },
  negative: { color: 'text-red-600', bgColor: 'bg-red-100' },
};

export default function SavingsRateCard({ savingsRate, formatCurrency, carouselElement }: SavingsRateCardProps) {
  const { t } = useTranslation('dashboard');
  const StatusIcon = statusIcons[savingsRate.status];
  const colors = statusColors[savingsRate.status];

  const statusLabels: Record<string, { label: string; description: string }> = {
    excellent: { label: t('savingsRateStatus.excellent'), description: t('savingsRateStatus.excellentDesc') },
    good: { label: t('savingsRateStatus.good'), description: t('savingsRateStatus.goodDesc') },
    average: { label: t('savingsRateStatus.average'), description: t('savingsRateStatus.averageDesc') },
    low: { label: t('savingsRateStatus.low'), description: t('savingsRateStatus.lowDesc') },
    negative: { label: t('savingsRateStatus.negative'), description: t('savingsRateStatus.negativeDesc') },
  };
  const config = { ...colors, ...statusLabels[savingsRate.status] };

  // Calculer le pourcentage pour la jauge (limité entre 0 et 100)
  const gaugePercent = Math.max(0, Math.min(100, savingsRate.rate + 50)); // Centré à 50%

  return (
    <div className="card h-full">
      {carouselElement}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{t('savingsRate.title')}</h3>
        <div className={clsx('p-2 rounded-lg', config.bgColor)}>
          <PiggyBank className={clsx('w-5 h-5', config.color)} />
        </div>
      </div>

      <div className="text-center mb-4">
        <div className={clsx('text-4xl font-bold mb-1', config.color)}>
          {savingsRate.rate >= 0 ? '+' : ''}{savingsRate.rate.toFixed(1)}%
        </div>
        <p className="text-sm text-gray-600">
          {savingsRate.amount >= 0 ? t('savingsRateStatus.saved') : t('savingsRateStatus.deficit')}
          <span className={clsx('font-medium', savingsRate.amount >= 0 ? 'text-green-600' : 'text-red-600')}>
            {formatCurrency(Math.abs(savingsRate.amount))}
          </span>
        </p>
      </div>

      {/* Jauge visuelle */}
      <div className="mb-4">
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-500',
              savingsRate.rate >= 20 ? 'bg-green-500' :
              savingsRate.rate >= 10 ? 'bg-emerald-500' :
              savingsRate.rate >= 0 ? 'bg-amber-500' :
              savingsRate.rate >= -10 ? 'bg-orange-500' : 'bg-red-500'
            )}
            style={{ width: `${gaugePercent}%` }}
          />
        </div>
        <div className="flex justify-between mt-1 text-xs text-gray-500">
          <span>-50%</span>
          <span>0%</span>
          <span>+50%</span>
        </div>
      </div>

      {/* Statut */}
      <div className={clsx('flex items-center gap-2 p-3 rounded-lg', config.bgColor)}>
        <StatusIcon className={clsx('w-5 h-5 flex-shrink-0', config.color)} />
        <div>
          <p className={clsx('font-medium text-sm', config.color)}>{config.label}</p>
          <p className="text-xs text-gray-600">{config.description}</p>
        </div>
      </div>
    </div>
  );
}
